/**
 * Google Identity Services（GIS）Web SDK 整合
 * -----------------------------------------------------------------------------
 * 我們**不用** Firebase Auth SDK；改用 Google 官方的 GIS Web SDK 取得 id_token，
 * 再交給後端 `/api/front_auth/google_login` 驗證。本檔做三件事：
 *
 * 1. 動態載入 ``https://accounts.google.com/gsi/client``（避免拖慢首屏）。
 * 2. 用 ``client_id`` 初始化 GIS。
 * 3. 將官方按鈕 render 到指定 DOM 節點，並用 callback 把 id_token 拋出來。
 *
 * 文件：https://developers.google.com/identity/gsi/web/reference/js-reference
 */

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/**
 * GIS callback 收到的最小欄位。
 *
 * @property credential id_token JWT 字串。
 * @property select_by  使用者觸發方式（``btn`` / ``user_1tap`` 等），通常不需要。
 */
export interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

/**
 * 與本檔互動所需的 GIS API 子集（避免引入完整 ambient types）。
 */
interface GsiClient {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        ux_mode?: 'popup' | 'redirect';
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type?: 'standard' | 'icon';
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'large' | 'medium' | 'small';
          text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
          shape?: 'rectangular' | 'pill' | 'circle' | 'square';
          width?: number;
          locale?: string;
        },
      ): void;
      prompt(): void;
      disableAutoSelect(): void;
    };
  };
}

declare global {
  interface Window {
    google?: GsiClient;
  }
}

let scriptPromise: Promise<void> | null = null;

/**
 * 動態載入 GIS 的 ``client.js``；多次呼叫只載一次（cached promise）。
 *
 * @returns 載完後 resolve 的 Promise。
 *
 * @example
 *   await loadGoogleIdentityScript();
 *   // 此後 window.google.accounts.id 可用
 */
export function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('GIS is browser-only (window is undefined)'));
  }
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SCRIPT_SRC}"]`,
    );
    const script = existing ?? document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      scriptPromise = null;
      reject(new Error('failed to load GIS script'));
    });
    if (!existing) document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * 在指定 DOM 節點上 render 官方 Google 登入按鈕。
 *
 * 點下按鈕後，GIS 會把 id_token 透過 ``onCredential`` 拋出。
 * 失敗（例如 ``clientId`` 為空、SDK 載入失敗）會在 Promise 端 reject，
 * 呼叫端通常將其轉為 i18n 錯誤訊息顯示在 UI。
 *
 * @param target      要塞入按鈕的 DOM 元素（建議是空白 ``<div>``）。
 * @param clientId    Google OAuth client_id；目前由
 *                    ``services.authProvider.getGoogleConfig()`` 從後端取得。
 * @param onCredential 收到 id_token 時要做的事；典型流程是
 *                     ``services.authProvider.signIn(idToken)``。
 * @param options     按鈕外觀（locale、theme、size 等），可省略。
 *
 * @example
 *   await renderGoogleSignInButton(divRef.current!, clientId, async (idToken) => {
 *     const session = await services.authProvider.signIn(idToken);
 *     setSession(session);
 *   });
 */
export async function renderGoogleSignInButton(
  target: HTMLElement,
  clientId: string,
  onCredential: (idToken: string) => void | Promise<void>,
  options: {
    locale?: string;
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    width?: number;
  } = {},
): Promise<void> {
  if (!clientId) throw new Error('clientId is empty — check backend /api/front_auth/google/config response');
  await loadGoogleIdentityScript();
  const gsi = window.google;
  if (!gsi) throw new Error('window.google missing after GIS script load');

  gsi.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      void onCredential(response.credential);
    },
    auto_select: false,
    ux_mode: 'popup',
  });
  gsi.accounts.id.renderButton(target, {
    type: 'standard',
    theme: options.theme ?? 'outline',
    size: options.size ?? 'large',
    text: options.text ?? 'signin_with',
    shape: options.shape ?? 'rectangular',
    locale: options.locale ?? 'zh_TW',
    ...(options.width ? { width: options.width } : {}),
  });
}

/**
 * 測試輔助：清除已快取的腳本載入 Promise。
 *
 * 正式程式碼請勿呼叫；只在 unit test 之間需要 reset 模組狀態時使用。
 *
 * @example
 *   // vitest beforeEach
 *   resetGoogleIdentityForTest();
 */
export function resetGoogleIdentityForTest(): void {
  scriptPromise = null;
}
